import { displayName, getEvidenceName } from "~/common/functions";
import type { EvidencesBoolExp, EvidencesOrderBy, OrderBy } from "~/common/types/graphql";
import type { GetEvidencesCountQueryVariables, GetEvidencesQueryVariables } from "~/features/evidences/gqls";
import type {
  EvidenceComposition,
  EvidenceDetailRaw,
  EvidenceDetailUI,
  EvidenceListFilters,
  EvidenceRaw,
  EvidenceRow,
  EvidenceVersionRow,
  SortDirection,
} from "~/features/evidences/types";
import type { Locale } from "~/i18n/resolveLocale";

const evidenceName = (properties: unknown): string =>
  getEvidenceName({ properties: (properties ?? {}) as Record<string, unknown> });

function toEvidenceRow(raw: EvidenceRaw): EvidenceRow {
  const reference = raw.reference;
  return {
    id: raw.id,
    date: (raw.properties as { date?: string } | null)?.date ?? "",
    name: evidenceName(raw.properties),
    referenceShortCode: reference?.shortCode ?? "",
    referenceTitle: reference?.title ?? "",
    userName: raw.user?.displayName ?? raw.user?.email ?? "",
    updatedAt: raw.updatedAt,
    isDeactivated: Boolean(raw.deactivatedAt),
  };
}

export function toEvidenceRowList(raws: readonly EvidenceRaw[]): EvidenceRow[] {
  return raws.map(toEvidenceRow);
}

/** Translate the UI filter shape into a Hasura `EvidencesBoolExp` (V1 parity). */
function buildEvidencesWhere(filters: EvidenceListFilters): EvidencesBoolExp {
  const andList: EvidencesBoolExp[] = [];
  if (filters.referenceIds.length > 0) {
    andList.push({ referenceId: { _in: filters.referenceIds } });
  }
  if (filters.userId) {
    andList.push({ userId: { _eq: filters.userId } });
  }
  return { _and: andList };
}

const orderValue = (direction: SortDirection): OrderBy => (direction === "asc" ? "ASC" : "DESC");

/** Assemble the full `GetEvidences` query variables for a page of the list. */
export function buildEvidencesVariables(args: {
  tenantId: string;
  filters: EvidenceListFilters;
  direction: SortDirection;
  pageIndex: number;
  pageSize: number;
}): GetEvidencesQueryVariables {
  const { tenantId, filters, direction, pageIndex, pageSize } = args;
  return {
    tenantId,
    // Fetch one extra row so pagination can detect a next page without an aggregate count.
    limit: pageSize + 1,
    offset: pageIndex * pageSize,
    where: buildEvidencesWhere(filters),
    order_by: { updatedAt: orderValue(direction) } satisfies EvidencesOrderBy,
  };
}

/** Assemble the count query variables using the same filters as the list query. */
export function buildEvidencesCountVariables(args: {
  tenantId: string;
  filters: EvidenceListFilters;
}): GetEvidencesCountQueryVariables {
  return {
    tenantId: args.tenantId,
    where: buildEvidencesWhere(args.filters),
  };
}

/**
 * Treat the over-fetched list result as authoritative for navigation. The cached
 * count is display-only and must never shrink the range below the current page.
 */
export function deriveEvidencePagination(args: {
  totalCount: number | undefined;
  rowsLength: number;
  pageIndex: number;
  pageSize: number;
}): { rowCount: number; hasNextPage: boolean } {
  const lowerBound = args.pageIndex * args.pageSize + Math.min(args.rowsLength, args.pageSize);
  return {
    rowCount: Math.max(args.totalCount ?? 0, lowerBound),
    hasNextPage: args.rowsLength > args.pageSize,
  };
}

// ── Detail ────────────────────────────────────────────────────────────────────

type EvidenceVersionRaw = EvidenceDetailRaw["evidenceVersions"][number];

const toVersionRow = (v: EvidenceVersionRaw): EvidenceVersionRow => ({
  id: v.id,
  version: v.version,
  operation: v.operation,
  page: (v.citation as { page?: string } | null)?.page ?? null,
  referenceId: v.referenceId,
  isDeactivated: Boolean(v.deactivatedAt),
  createdAt: v.createdAt,
  properties: v.properties,
  citation: v.citation,
  metadata: v.metadata,
});

/** Shape the evidence detail (Data tab) — reference, activity, compositions. */
export function toEvidenceDetailUI(raw: EvidenceDetailRaw, locale: Locale): EvidenceDetailUI {
  const reference = raw.reference;

  const sets = raw.evidenceEvidenceSets.map((ees) => ees.evidenceSet);
  const setForBeJob = sets.find((s) => s.batchEvidencesJobs.length > 0) ?? null;
  const setsForBcrJobs = sets.filter((s) => s.batchChangeRequestsJobs.length > 0);
  const changeRequests = raw.evidenceChangeRequests.map((ecr) => ecr.changeRequest);

  const compositions: EvidenceComposition[] = setsForBcrJobs.flatMap((set) =>
    set.batchChangeRequestsJobs.flatMap((job) => {
      const cr = changeRequests.find((c) => c.batchChangeRequestsJobId === job.id);
      if (!cr) return [];
      const records = cr.items.map((item) => item.changeRequestItemRecord?.record).filter((r) => r != null);
      return [
        {
          key: `${set.id}:${job.id}`,
          dataImportProjectId: job.dataImportProject?.id ?? null,
          dataImportProjectName: job.dataImportProject?.name ?? "",
          jobId: job.id,
          jobName: job.name ?? "",
          jobRunId: cr.batchChangeRequestsJobRunId ?? null,
          changeRequestId: cr.id,
          statusId: cr.statusId,
          recordMetadataIds: cr.items
            .map((item) => item.changeRequestItemRecordMetadata?.recordMetadata?.id)
            .filter((id): id is string => id != null),
          entityRecords: records
            .filter((r) => r.entityRecord)
            .map((r) => ({ id: r.id, name: displayName({ name: r.name, nameJa: r.nameJa }, locale) || r.id })),
          relationRecords: records
            .filter((r) => r.relationRecord)
            .map((r) => ({ id: r.id, name: displayName({ name: r.name, nameJa: r.nameJa }, locale) || r.id })),
        },
      ];
    }),
  );

  return {
    id: raw.id,
    name: evidenceName(raw.properties) || raw.id,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
    deactivatedAt: raw.deactivatedAt ?? null,
    isDeactivated: Boolean(raw.deactivatedAt),
    reference: reference ? { id: reference.id, title: reference.title } : null,
    citation: raw.citation,
    properties: raw.properties,
    metadata: raw.metadata,
    activity: setForBeJob
      ? {
          setId: setForBeJob.id,
          setName: setForBeJob.name ?? "",
          jobs: setForBeJob.batchEvidencesJobs.map((j) => ({
            id: j.id,
            name: j.name ?? "",
            dataImportProjectId: j.dataImportProject?.id ?? null,
            dataImportProjectName: j.dataImportProject?.name ?? "",
            dataSourceId: j.dataSource?.id ?? null,
            dataSourceName: j.dataSource?.name ?? "",
          })),
        }
      : null,
    compositions,
    headVersion: raw.version,
    versions: raw.evidenceVersions.map(toVersionRow),
  };
}
